import MarkdownIt from "markdown-it";
import DOMPurify from "dompurify";

export interface AIEntityRef {
  type:
    | "resource"
    | "campaign"
    | "page"
    | "search_term"
    | "dashboard"
    | "goal"
    | "kpi";
  id: number;
  name: string;
  channel?: string;
}

export interface ParsedAIContent {
  text: string;
  entities: AIEntityRef[];
  suggestions: string[];
}

/**
 * Parse a `---context` JSON block from AI response and return
 * clean text + structured entity references.
 *
 * The AI outputs:
 * ```
 * Some markdown text...
 * ---context
 * {"resources": [...], "campaigns": [...], "pages": [...]}
 * ---
 * ```
 */
export function parseAIContent(content: string): ParsedAIContent {
  // Extract suggestions block first (may appear after context block)
  // Closing --- delimiter is optional (LLM sometimes omits it)
  const suggestionsMatch = content.match(
    /---suggestions\s*\n([\s\S]*?)(?:\n---\s*$|$)/,
  );
  let suggestions: string[] = [];
  let remaining = content;

  if (suggestionsMatch) {
    remaining = content.slice(0, suggestionsMatch.index).trimEnd();
    try {
      const parsed = JSON.parse(suggestionsMatch[1].trim());
      if (Array.isArray(parsed)) {
        suggestions = parsed.filter(
          (s): s is string => typeof s === "string" && s.length > 0,
        );
      }
    } catch {
      // JSON parse failed — skip suggestions
    }
  }

  const contextMatch = remaining.match(
    /---context\s*\n([\s\S]*?)(?:\n---\s*$|$)/,
  );

  if (!contextMatch) {
    return { text: remaining, entities: [], suggestions };
  }

  const text = remaining.slice(0, contextMatch.index).trimEnd();
  const entities: AIEntityRef[] = [];

  try {
    const parsed = JSON.parse(contextMatch[1].trim());

    if (Array.isArray(parsed.resources)) {
      for (const r of parsed.resources) {
        if (r.id && r.name) {
          entities.push({
            type: "resource",
            id: r.id,
            name: r.name,
            channel: r.channel,
          });
        }
      }
    }
    if (Array.isArray(parsed.campaigns)) {
      for (const c of parsed.campaigns) {
        if (c.id && c.name) {
          entities.push({
            type: "campaign",
            id: c.id,
            name: c.name,
            channel: c.channel,
          });
        }
      }
    }
    if (Array.isArray(parsed.pages)) {
      for (const p of parsed.pages) {
        if (p.id && p.url) {
          entities.push({
            type: "page",
            id: p.id,
            name: p.url,
          });
        }
      }
    }
    if (Array.isArray(parsed.dashboards)) {
      for (const d of parsed.dashboards) {
        if (d.id && d.name) {
          entities.push({
            type: "dashboard",
            id: d.id,
            name: d.name,
          });
        }
      }
    }
    if (Array.isArray(parsed.goals)) {
      for (const g of parsed.goals) {
        if (g.id && g.name) {
          entities.push({
            type: "goal",
            id: g.id,
            name: g.name,
          });
        }
      }
    }
    if (Array.isArray(parsed.kpis)) {
      for (const k of parsed.kpis) {
        if (k.id && k.name) {
          entities.push({
            type: "kpi",
            id: k.id,
            name: k.name,
          });
        }
      }
    }
  } catch {
    // JSON parse failed — just return text without entities
  }

  return { text, entities, suggestions };
}

const md = new MarkdownIt({ linkify: true, breaks: true });

// Open all links in a new tab
const defaultLinkRender =
  md.renderer.rules.link_open ||
  ((tokens, idx, options, _env, self) =>
    self.renderToken(tokens, idx, options));

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  tokens[idx].attrSet("target", "_blank");
  tokens[idx].attrSet("rel", "noopener noreferrer");
  return defaultLinkRender(tokens, idx, options, env, self);
};

export function renderMarkdown(content: string): string {
  return DOMPurify.sanitize(md.render(content), {
    ADD_ATTR: ["target"],
  });
}

// Newlines collapse to spaces so multi-line AI content stays on a
// single preview line in list/card summaries — block elements would
// break the compact layout `renderMarkdown` is built for.
export function renderMarkdownInline(content: string): string {
  const singleLine = content.replace(/[\r\n]+/g, " ").trim();
  return DOMPurify.sanitize(md.renderInline(singleLine), {
    ADD_ATTR: ["target"],
  });
}
